import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useMemo } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import Select from "react-select";
import { z } from "zod";
import { toast } from "react-toastify";

import { addCustomer, updateCustomer } from "~/services/supervisor.services";

import { SubmitButton as Button } from "~/components/form/submit-button";
import { cn } from "~/utils/tailwind";
import type { ReportConfig, ReportColumnConfig } from "../masterConfigs";

/* ------------------------------------------------------------------ */
/*  Fully generic add/edit form.                                       */
/*                                                                      */
/*  There is no per-master field list here anymore — every field       */
/*  (type, required, maxLength, options, apiKeys, disabled rules) is    */
/*  read straight from `config.columns` (see masterConfigs.tsx).       */
/*  Schema, default values, API-response mapping, submit payload and    */
/*  the UI are all derived from it, so a brand new master with a        */
/*  different set of fields works out of the box — just add its column  */
/*  config, no changes needed in this file.                             */
/*                                                                      */
/*  Only set a custom `ModalComponent` in masterConfigs.tsx if a master */
/*  needs UI this generic form genuinely can't express.                 */
/* ------------------------------------------------------------------ */

type CustomerModalMode = "add" | "edit";

type FormValues = {
  data: { customer_details: Record<string, unknown>[] };
};

const getFormFields = (config: ReportConfig): ReportColumnConfig[] =>
  config.columns.filter((c) => c.showInForm !== false);

/* ---------------------------- schema -------------------------------- */

const buildTextSchema = (f: ReportColumnConfig): z.ZodTypeAny => {
  let schema = z.string();

  if (f.maxLength) {
    schema = schema.max(f.maxLength, `${f.header} should be up to ${f.maxLength} characters`);
  }
  if (f.pattern) {
    schema = schema.regex(f.pattern, f.patternMessage ?? `${f.header} format is invalid`);
  }
  if (f.required) {
    return schema
      .min(1, `${f.header} is required`)
      .refine((val) => val.trim().length > 0, `${f.header} cannot be only spaces`);
  }
  return schema;
};

const buildNumberSchema = (f: ReportColumnConfig): z.ZodTypeAny =>
  z.preprocess(
    (val) => (val === "" || val === null || val === undefined ? null : Number(val)),
    z
      .number({ invalid_type_error: `${f.header} must be a number` })
      .nullable()
      .refine((val) => val === null || Number.isInteger(val), `${f.header} must be a whole number`)
      .refine((val) => val === null || val >= 0, `${f.header} cannot be negative`)
      .refine(
        (val) => val === null || String(val).length <= (f.maxLength ?? 10),
        `${f.header} should be up to ${f.maxLength ?? 10} digits`
      )
  );

const buildFieldSchema = (f: ReportColumnConfig): z.ZodTypeAny =>
  f.fieldType === "number" ? buildNumberSchema(f) : buildTextSchema(f);

// "id" rides along via .passthrough() without its own validation rule —
// it's only used internally to know which record is being edited.
const buildFormSchema = (fields: ReportColumnConfig[]) => {
  const shape = fields
    .filter((f) => f.key !== "id")
    .reduce((acc, f) => {
      acc[f.key] = buildFieldSchema(f);
      return acc;
    }, {} as Record<string, z.ZodTypeAny>);

  return z.object({
    data: z.object({
      customer_details: z.array(z.object(shape).passthrough()),
    }),
  });
};

/* ------------------------ defaults + mapping -------------------------- */

const getDefaultValues = (fields: ReportColumnConfig[]): FormValues => ({
  data: {
    customer_details: [
      fields.reduce((acc, f) => {
        if (f.defaultValue !== undefined) {
          acc[f.key] = f.defaultValue;
        } else {
          acc[f.key] = f.fieldType === "number" ? null : "";
        }
        return acc;
      }, {} as Record<string, unknown>),
    ],
  },
});

const pickValue = (source: any, f: ReportColumnConfig) => {
  for (const key of [f.key, ...(f.apiKeys ?? [])]) {
    const raw = source?.[key];
    if (raw !== undefined && raw !== null) {
      return f.fieldType === "select" ? String(raw) : raw;
    }
  }
  return f.fieldType === "number" ? null : "";
};

const mapResponseToForm = (data: any, fields: ReportColumnConfig[]): FormValues => {
  const record =
    data?.data?.customer_details?.[0] || data?.data?.customers?.[0] || data?.data || data;
  return {
    data: {
      customer_details: [
        fields.reduce((acc, f) => {
          acc[f.key] = pickValue(record, f);
          return acc;
        }, {} as Record<string, unknown>),
      ],
    },
  };
};

/* ------------------------------ field UI ------------------------------ */

const ALLOWED_NUMERIC_CONTROL_KEYS = new Set([
  "Backspace", "Delete", "Tab", "Escape", "Enter",
  "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
  "Home", "End",
]);

const RESTRICTED_NUMERIC_FIELDS = new Set([
  "dominant_account_percentage",
  "validation_threshold",
]);

const handleNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return; // let copy/paste/select-all etc. through
  if (ALLOWED_NUMERIC_CONTROL_KEYS.has(e.key)) return;
  if (/^[0-9]$/.test(e.key)) return;
  e.preventDefault();
};

const customeValidation = (
  field?: string,
  e?: React.KeyboardEvent<HTMLInputElement>
) => {
  if (!field || !e) return;

  if (RESTRICTED_NUMERIC_FIELDS.has(field)) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (ALLOWED_NUMERIC_CONTROL_KEYS.has(e.key)) return;

    if (e.key === ".") {
      const target = e.target as HTMLInputElement;
      if (target.value.includes(".")) e.preventDefault(); // sirf ek dot allow
      return;
    }

    if (/^[0-9]$/.test(e.key)) return;

    e.preventDefault();
  }
};

const handleNumericPaste = (
  e: React.ClipboardEvent<HTMLInputElement>,
  onChange: (val: number | null) => void
) => {
  e.preventDefault();
  const digitsOnly = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
  onChange(digitsOnly === "" ? null : Number(digitsOnly));
};

const FieldRenderer: React.FC<{ field: ReportColumnConfig; idx: number; isEdit: boolean }> = ({
  field: f,
  idx,
  isEdit,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();

  const message = (errors as any)?.data?.customer_details?.[idx]?.[f.key]?.message as
    | string
    | undefined;

  const disabledField = Boolean(f.alwaysDisabled || (isEdit && f.disabledInEdit));

  return (
    <div className="col-span-1 mb-0">
      <label className="block text-sm font-medium text-gray-700">
        {f.header}
        {f.required && <span className="text-red-500">*</span>}
      </label>

      <Controller
        name={`data.customer_details.${idx}.${f.key}` as any}
        control={control}
        render={({ field }) =>
          f.fieldType === "select" ? (
            <Select
              {...field}
              isClearable
              isSearchable
              isDisabled={disabledField}
              options={f.options}
              value={
                f.options?.find(
                  (opt) => String(opt.value).trim() === String(field.value ?? "").trim()
                ) ?? null
              }
              onChange={(newValue: any) => field.onChange(newValue?.value ?? "")}
            />
          ) : (
            <input
              {...field}
              value={field.value ?? ""}
              type={f.fieldType === "number" ? "number" : "text"}
              inputMode={f.fieldType === "number" ? "numeric" : undefined}
              min={f.fieldType === "number" ? 0 : undefined}
              placeholder={f.header}
              className={cn(
                "mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm",
                message ? "border-red-700 border-solid border-2" : "",
                disabledField ? "bg-gray-200 cursor-not-allowed" : ""
              )}
              onChange={(e) => {
                const val = e.target.value;
                field.onChange(f.fieldType === "number" ? (val === "" ? null : Number(val)) : val);
              }}
              onKeyDown={
                f.fieldType === "number"
                  ? handleNumericKeyDown
                  : (e: React.KeyboardEvent<HTMLInputElement>) => customeValidation(f.key, e)
              }
              onPaste={
                f.fieldType === "number"
                  ? (e) => handleNumericPaste(e, field.onChange)
                  : undefined
              }
              disabled={disabledField}
            />
          )
        }
      />

      {message && <p className="mt-1 text-xs text-red-500">{message}</p>}
    </div>
  );
};

/* -------------------------------- modal -------------------------------- */

const MasterFormModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: CustomerModalMode;
  selectedData?: object | null;
  config: ReportConfig;
}> = ({ isOpen, onClose, onSuccess, mode, selectedData, config }) => {
  const isEdit = mode === "edit";

  const formFields = useMemo(() => getFormFields(config), [config]);
  const FormSchema = useMemo(() => buildFormSchema(formFields), [formFields]);

  const form = useForm<FormValues>({
    mode: "onChange",
    resolver: zodResolver(FormSchema),
    defaultValues: getDefaultValues(formFields),
  });

  const { handleSubmit, control } = form;

  const { fields } = useFieldArray({
    control,
    name: "data.customer_details",
  });

  useEffect(() => {
    if (isOpen && isEdit) {
      form.reset(mapResponseToForm(selectedData, formFields));
    }
    if (isOpen && !isEdit) {
      form.reset(getDefaultValues(formFields));
    }

  }, [isOpen, isEdit, config.key]);

  const onSubmit = async (data: FormValues) => {
    const record = data.data.customer_details[0] as any;

    const payload = formFields.reduce((acc, f) => {
      acc[f.key] = f.key === "id" ? (isEdit ? Number(record.id) : "") : record[f.key];
      return acc;
    }, {} as Record<string, unknown>);

    const response = await (isEdit
      ? updateCustomer(payload, record.id, config.key)
      : addCustomer(payload, config.key));
    const responseData = response.data as any;

    if (response.status) {
      const spRow = responseData?.data?.[0];
      const errorMessage = spRow?.errormessage || "";

      if (typeof responseData?.data === "string") {

        const { type, message } = JSON.parse(responseData?.data);

        if (message) {
          if (type.toLowerCase() === "error") {
            toast.error(message, { theme: "colored" });
          } else if (type.toLowerCase() === "success") {
            toast.success(message, { theme: "colored" });
          }
        }
        onSuccess();
        onClose();
        return;
      }

      if (!isEdit && errorMessage.includes("Updated")) {
        toast.info(`${config.title} already exists`, { theme: "colored" });
        onSuccess();
        onClose();
        return;
      }

      toast.success(isEdit ? "Record updated successfully" : "Record added successfully", {
        theme: "colored",
      });
      onSuccess();
      onClose();
    } else {
      toast.error(responseData?.message || "Something went wrong", { theme: "colored" });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50 ">
      <div className="bg-white rounded-lg p-6 max-w-7xl w-full" style={{ zoom: "95%" }}>
        <h2 className="text-xl font-bold mb-4 flex justify-center">
          {isEdit ? "Edit" : "Add"} {config.title}
        </h2>

        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {fields.map((item, idx) => (
              <div className="grid grid-cols-5 gap-4" key={item.id}>
                {formFields.map((f) => (
                  <FieldRenderer key={f.key} field={f} idx={idx} isEdit={isEdit} />
                ))}
              </div>
            ))}

            <div className="mt-4 flex justify-end gap-2">
              <Button role="button" variant="secondary" radius="full" size="medium" onClick={onClose}>
                Cancel
              </Button>
              <Button role="button" radius="full" size="medium" type="submit">
                {isEdit ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default MasterFormModal;